import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const ReturnPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Return Policy</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            We want you to be completely satisfied with your purchase. Learn about our return and refund policies.
          </p>
        </div>
      </section>

      {/* Policy Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Return Policy</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a 30-day return window for most items. Custom designs and personalized products 
              may have different return terms. Please read the details below.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">30-Day Returns</h3>
              <p className="text-gray-600">
                Most items can be returned within 30 days of delivery for a full refund.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TruckIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Free Returns</h3>
              <p className="text-gray-600">
                We cover return shipping costs for items that arrive damaged or defective.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowPathIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy Process</h3>
              <p className="text-gray-600">
                Start your return online and we'll guide you through the entire process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Return Conditions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Return Conditions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Eligible for Returns */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircleIcon className="w-6 h-6 text-green-600 mr-2" />
                Eligible for Returns
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Items received damaged or defective
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Wrong item received
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Standard products (non-customized)
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Items in original packaging
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Returned within 30 days of delivery
                </li>
              </ul>
            </div>

            {/* Not Eligible for Returns */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <XCircleIcon className="w-6 h-6 text-red-600 mr-2" />
                Not Eligible for Returns
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <XCircleIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  Custom or personalized designs
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  Items used or worn
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  Items missing original packaging
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  Digital products or downloads
                </li>
                <li className="flex items-start">
                  <XCircleIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  Items returned after 30 days
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Return Process */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How to Return an Item</h2>
          
          <div className="space-y-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-4">
                1
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Your Return</h3>
                <p className="text-gray-600">
                  Log into your account and go to "My Orders". Find the order you want to return 
                  and click "Return Item". You can also contact our customer service team.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-4">
                2
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Package Your Item</h3>
                <p className="text-gray-600">
                  Ensure the item is in its original condition and packaging. Include all original 
                  accessories, tags, and documentation. Use a sturdy box for shipping.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-4">
                3
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ship Your Return</h3>
                <p className="text-gray-600">
                  Use the return shipping label we provide, or ship to our return address. 
                  For damaged or defective items, we'll cover the shipping costs.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-4">
                4
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Receive Your Refund</h3>
                <p className="text-gray-600">
                  Once we receive and inspect your return, we'll process your refund within 5-7 business days. 
                  You'll receive an email confirmation when the refund is issued.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Refund Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Refund Information</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Refund Timeline</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-2" />
                    <span>Return received: 1-2 business days</span>
                  </li>
                  <li className="flex items-center">
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-2" />
                    <span>Inspection period: 1-2 business days</span>
                  </li>
                  <li className="flex items-center">
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-2" />
                    <span>Refund processing: 3-5 business days</span>
                  </li>
                  <li className="flex items-center">
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-2" />
                    <span>Total time: 5-7 business days</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Refund Methods</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Original payment method</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Store credit (if requested)</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Exchange for different item</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Questions About Returns?</h2>
          <p className="text-gray-600 mb-8">
            Our customer service team is here to help with any questions about returns, exchanges, or refunds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-center"
            >
              Contact Support
            </Link>
            <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors">
              Start a Return
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReturnPolicy; 